<script lang="ts">
	import Carousel from '$lib/carousel.svelte';
	import type { EmblaCarouselType } from 'embla-carousel';
	import ArrowLeft from '$lib/icons/arrowLeft.svelte';
	import ArrowRight from '$lib/icons/arrowRight.svelte';
	import TimedGalleryIndicator from '$lib/timedGalleryIndicator.svelte';
	import CarouselThumbnails from '../parts/carouselThumbnails.svelte';
	import { checkVisibility } from '$lib/actions/checkVisibility';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '../editable';
	import Blok from '../blok.svelte';
	import type { SbVideo } from '../types';
	import type { ImageBlock } from './imageBlock.svelte';

	interface Props {
		content: ISbStoryData<{
			assets: Array<ISbStoryData<ImageBlock | SbVideo>['content']>;
			autoplay: boolean;
		}>['content'];
	}

	let { content }: Props = $props();

	let active = $state(0);

	let embla = $state<EmblaCarouselType>();

	function next() {
		if (embla) {
			embla.canScrollNext() ? embla.scrollNext() : embla.scrollTo(0);
		}
	}

	function previous() {
		if (embla) {
			embla.canScrollPrev() ? embla.scrollPrev() : embla.scrollTo(content.assets.length - 1);
		}
	}

	let autoplay = $state(false);
	let firstImageIndex = $derived(content.assets.findIndex((item) => item.component === 'image'));
</script>

<div
	use:editable={content}
	use:checkVisibility={{ threshold: 0.8 }}
	onvisible={({ detail }) => (autoplay = detail && content.autoplay)}
	class="group relative col-span-full overflow-hidden max-lg:w-screen lg:col-start-1 lg:col-end-11 lg:-ml-8 lg:w-[calc(100%+32px)]"
>
	<div class="relative overflow-hidden lg:rounded-3xl">
		<Carousel
			bind:embla
			bind:currentIndex={active}
			{autoplay}
			autoplayDuration={4000}
			class="flex"
			align="center"
		>
			{#each content.assets as item, index}
				{@const isFirstImage = index === firstImageIndex && item.component === 'image'}
				<div
					class="aspect-4/3 w-full flex-none basis-full"
					{...isFirstImage ? { itemprop: 'image', content: item.asset.filename } : {}}
				>
					<Blok content={item} />
				</div>
			{/each}
		</Carousel>

		{#if embla}
			<div class="absolute bottom-3 left-1/2 -translate-x-1/2 lg:bottom-4">
				<TimedGalleryIndicator {embla} {active} count={content.assets.length} />
			</div>
		{/if}

		{#if content.assets.length > 3}
			<button
				onclick={previous}
				class="absolute left-4 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-stone-5 p-4 opacity-0 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] transition-opacity duration-500 disabled:opacity-0 max-lg:hidden lg:group-hover:opacity-100"
			>
				<ArrowLeft class="h-6 w-6" />
			</button>
			<button
				onclick={next}
				class="absolute right-4 top-1/2 flex -translate-y-1/2 items-center justify-center rounded-full bg-stone-5 p-4 opacity-0 shadow-[0_0_8px_0_rgba(0,0,0,0.12)] transition-opacity duration-500 disabled:opacity-0 max-lg:hidden lg:group-hover:opacity-100"
			>
				<ArrowRight class="h-6 w-6" />
			</button>
		{/if}
	</div>
	<div class="mt-2 flex justify-center lg:mt-4">
		<CarouselThumbnails
			{active}
			onselect={(index) => embla?.scrollTo(index)}
			assets={content.assets}
		/>
	</div>
</div>
